import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { defaultPageSize } from "../../constants/app";

import { type APIConfig, type ApiPagination } from "../../types/api";
import { type PropertyFull, type Property } from "../../types/property";
import { type PayloadActionForAPI } from "../../types/app";
import { type Workspace } from "../../types/workspace";

export type PropertiesSliceState = {
   list?: ApiPagination<PropertyFull>;
   workspaceProperties?: Record<Workspace["id"], ApiPagination<PropertyFull> | undefined>;
};

const initialState: PropertiesSliceState = {};

export const propertiesSlice = createSlice({
   name: "properties",
   initialState,
   reducers: {
      getAllProperties: (_, __: PayloadActionForAPI<APIConfig["getAllProperties"], {}, true>) => {},
      getPropertyById: (
         _,
         __: PayloadActionForAPI<
            APIConfig["getPropertyById"],
            {
               id: Property["id"];
            }
         >,
      ) => {},
      setProperties: (state, action: PayloadAction<PropertiesSliceState["list"]>) => {
         state.list = action.payload;
      },
      createProperty: (_, __: PayloadActionForAPI<APIConfig["createProperty"]>) => {},
      editProperty: (
         _,
         __: PayloadActionForAPI<
            APIConfig["editProperty"],
            {
               id: Property["id"];
            }
         >,
      ) => {},
      propertyOperations: (
         _,
         __: PayloadActionForAPI<
            APIConfig["propertyOperations"],
            {
               id: Property["id"];
            }
         >,
      ) => {},
      updateProperty: (
         state,
         action: PayloadAction<
            | NonNullable<PropertiesSliceState["list"]>["items"][number]
            | NonNullable<PropertiesSliceState["list"]>["items"][number][]
         >,
      ) => {
         const newItems = Array.isArray(action.payload) ? action.payload : [action.payload];

         if (!state.list)
            state.list = {
               items: [],
               page: 1,
               pageSize: defaultPageSize,
               total: 0,
            };

         for (let index = 0; index < newItems.length; index++) {
            const newItem = newItems[index];
            const stateListIndex = state.list.items.findIndex((item) => item.property.id === newItem.property.id);

            if (stateListIndex !== -1)
               state.list.items[stateListIndex] = {
                  ...state.list.items[stateListIndex],
                  ...newItem,
               };
            else state.list.items.push(newItem);
         }
      },
      removeProperty: (
         state,
         action: PayloadAction<NonNullable<PropertiesSliceState["list"]>["items"][number]["property"]["id"]>,
      ) => {
         if (!state.list) return;

         state.list = {
            ...state.list,
            items: state.list.items.filter((item) => item.property.id !== action.payload),
         };
      },

      getAllWorkspaceProperties: (
         _,
         __: PayloadActionForAPI<
            APIConfig["getAllWorkspaceProperties"],
            {
               id: Workspace["id"];
            }
         >,
      ) => {},
      getWorkspacePropertyById: (
         _,
         __: PayloadActionForAPI<
            APIConfig["getWorkspacePropertyById"],
            {
               workspaceId: Workspace["id"];
               id: Property["id"];
            }
         >,
      ) => {},
      setWorkspaceProperties: (
         state,
         action: PayloadAction<{
            workspaceId: Workspace["id"];
            properties?: NonNullable<PropertiesSliceState["workspaceProperties"]>[Workspace["id"]];
         }>,
      ) => {
         if (!state.workspaceProperties) state.workspaceProperties = state.workspaceProperties = {};

         state.workspaceProperties[action.payload.workspaceId] = action.payload.properties;
      },
      addPropertyToWorkspace: (
         _,
         __: PayloadActionForAPI<
            APIConfig["addPropertyToWorkspace"],
            {
               id: Workspace["id"];
            }
         >,
      ) => {},
      updateWorkspaceProperty: (
         state,
         action: PayloadAction<{
            workspaceId: Workspace["id"];
            properties:
               | NonNullable<NonNullable<PropertiesSliceState["workspaceProperties"]>[Workspace["id"]]>["items"][number]
               | NonNullable<
                    NonNullable<PropertiesSliceState["workspaceProperties"]>[Workspace["id"]]
                 >["items"][number][];
         }>,
      ) => {
         const newItems = Array.isArray(action.payload.properties)
            ? action.payload.properties
            : [action.payload.properties];

         if (!state.workspaceProperties) state.workspaceProperties = {};

         if (!state.workspaceProperties[action.payload.workspaceId])
            state.workspaceProperties[action.payload.workspaceId] = {
               items: [],
               page: 1,
               pageSize: defaultPageSize,
               total: 0,
            };

         for (let index = 0; index < newItems.length; index++) {
            const newItem = newItems[index];
            const stateListIndex = state.workspaceProperties[action.payload.workspaceId]?.items.findIndex(
               (item) => item.property.id === newItem.property.id,
            );

            if (stateListIndex !== undefined && stateListIndex !== -1)
               state.workspaceProperties[action.payload.workspaceId]!.items[stateListIndex] = {
                  ...state.workspaceProperties[action.payload.workspaceId]?.items[stateListIndex],
                  ...newItem,
               };
            else state.workspaceProperties[action.payload.workspaceId]?.items.push(newItem);
         }
      },
   },
});
